/**
 * JS port of backend/utils/variant_weight.py :: resolve_variant_weight.
 *
 * Resolve a variant to its *actual* weight in grams so the dispatch
 * overlay reconciles indent and plan on SIZE, not on free-text labels.
 * Only truly equal weights merge - 500gm and 1000gm never collide.
 *
 * Cascade (first hit wins):
 *   1. variant_id -> packagings_by_id[vid].weight_gm
 *   2. UNIT variant ('Pieces' / 'Packets' / 'unit_piece' / 'unit_packet')
 *      -> retailerCatalogueEntry.purchase_weight_variant
 *      (or purchase_weights[0]) -> packagings_by_id[pw_id].weight_gm
 *   3. normalizeVariantKey(name) matches a packaging's normalised name
 *      -> that packaging's weight_gm
 *   4. Parse '<number> <unit>' out of the raw name (so '500+ gm',
 *      'Pack of 500 gm', '1 kg', '500 ml' still produce a number).
 *   5. null - unresolved.
 *
 * Keep this file in lockstep with backend/utils/variant_weight.py.
 */
import { normalizeVariantKey } from './variantKey.js';

const UNIT_IDS = new Set(['unit_piece', 'unit_packet']);
const UNIT_NAMES = new Set(['pieces', 'piece', 'packets', 'packet', 'pcs', 'pkts', 'pc', 'pkt']);

const WEIGHT_RE = /(\d+(?:\.\d+)?)\s*\+?\s*(kg|kgs|kilograms?|gm|gms|grams?|g|ml|mls|millilitres?|milliliters?|l|ltrs?|litres?|liters?)\b/i;

function isUnitVariant(variantId, variantName) {
  const vid = (variantId || '').trim().toLowerCase();
  const nm = (variantName || '').trim().toLowerCase();
  return UNIT_IDS.has(vid) || UNIT_NAMES.has(nm);
}

function parseWeightFromName(name) {
  if (!name) return null;
  const m = String(name).toLowerCase().match(WEIGHT_RE);
  if (!m) return null;
  const num = parseFloat(m[1]);
  if (!Number.isFinite(num)) return null;
  const unit = m[2].toLowerCase();
  if (unit.startsWith('kg') || unit.startsWith('kilogram') || unit.startsWith('kilo')) {
    return num * 1000;
  }
  if (unit === 'ml' || unit === 'mls' || unit.startsWith('millilitre') || unit.startsWith('milliliter')) {
    return num; // 1 ml ~= 1 g for match purposes
  }
  if (unit === 'l' || unit === 'ltr' || unit === 'ltrs' || unit.startsWith('litre') || unit.startsWith('liter')) {
    return num * 1000;
  }
  return num; // grams
}

/**
 * @param {string} variantName
 * @param {string} variantId
 * @param {Object} opts
 * @param {Object<string,{name:string,weight_gm:number}>} opts.packagingsById
 * @param {Object} [opts.retailerCatalogueEntry]  with purchase_weight_variant / purchase_weights
 * @returns {{ weight: number|null, source: string }}
 */
export function resolveVariantWeight(variantName, variantId, opts = {}) {
  const packagingsById = opts.packagingsById || {};
  const cat = opts.retailerCatalogueEntry || null;

  // 1. variant_id direct lookup
  if (variantId && packagingsById[variantId]) {
    const w = Number(packagingsById[variantId].weight_gm || 0);
    if (w > 0) return { weight: w, source: 'variant_id' };
  }

  // 2. Unit variant -> catalogue
  if (isUnitVariant(variantId, variantName) && cat) {
    let pwId = cat.purchase_weight_variant || '';
    if (!pwId) {
      const pw = cat.purchase_weights || [];
      if (pw.length > 0) pwId = pw[0];
    }
    if (pwId && packagingsById[pwId]) {
      const w = Number(packagingsById[pwId].weight_gm || 0);
      if (w > 0) return { weight: w, source: 'catalogue' };
    }
  }

  // 3. Normalised name match against packagings
  const nkey = normalizeVariantKey(variantName);
  if (nkey) {
    for (const pkg of Object.values(packagingsById)) {
      if (normalizeVariantKey(pkg.name || '') === nkey) {
        const w = Number(pkg.weight_gm || 0);
        if (w > 0) return { weight: w, source: 'name_normalized' };
      }
    }
  }

  // 4. Parse number+unit directly from the name
  const parsed = parseWeightFromName(variantName);
  if (parsed && parsed > 0) {
    return { weight: parsed, source: 'parsed_name' };
  }

  return { weight: null, source: 'unresolved' };
}

export default resolveVariantWeight;
